
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PublicHeader } from '@/components/PublicHeader';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Lock, Loader } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

const PasswordReset = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [validSession, setValidSession] = useState<boolean | null>(null);

  // Check if this is a valid password reset session
  useEffect(() => {
    const checkSession = async () => {
      console.log('[PasswordReset] Checking session validity');
      
      // Check for required URL params
      const accessToken = searchParams.get('access_token');
      const refreshToken = searchParams.get('refresh_token');
      const type = searchParams.get('type');
      
      if (!accessToken || !refreshToken || type !== 'recovery') {
        console.log('[PasswordReset] Invalid or missing recovery parameters');
        setValidSession(false);
        setError('Invalid password reset link. Please request a new password reset.');
        return;
      }

      try {
        // Set the session using the tokens from the URL
        const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
          access_token: accessToken,
          refresh_token: refreshToken
        });

        if (sessionError || !sessionData.session) {
          console.log('[PasswordReset] Session error:', sessionError);
          setValidSession(false);
          setError('Invalid or expired password reset link. Please request a new password reset.');
          return;
        }

        console.log('[PasswordReset] Valid password reset session established');
        setValidSession(true);
      } catch (err) {
        console.error('[PasswordReset] Error setting session:', err);
        setValidSession(false);
        setError('Failed to validate password reset link. Please try again.');
      }
    };

    checkSession();
  }, [searchParams]);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      console.log('[PasswordReset] Updating password');
      
      const { error: updateError } = await supabase.auth.updateUser({
        password: password
      });

      if (updateError) {
        console.log('[PasswordReset] Password update error:', updateError);
        setError(updateError.message);
        return;
      }

      console.log('[PasswordReset] Password updated successfully');
      setSuccess('Password updated successfully! Redirecting to dashboard...');
      
      // Wait a moment to show success message, then redirect
      setTimeout(() => {
        navigate('/app/dashboard', { replace: true });
      }, 2000);
      
    } catch (err) {
      console.error('[PasswordReset] Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestNewReset = () => {
    // Clear the current session and redirect to auth page
    supabase.auth.signOut();
    navigate('/auth?tab=reset', { replace: true });
  };

  if (validSession === null) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicHeader />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <LoadingSpinner message="Validating password reset link..." />
        </div>
      </div>
    );
  }

  if (validSession === false) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicHeader />
        
        <div className="flex items-center justify-center p-4 min-h-[calc(100vh-64px)]">
          <div className="w-full max-w-md">
            <Card>
              <CardHeader className="text-center">
                <CardTitle className="text-2xl">Password Reset</CardTitle>
                <CardDescription>
                  There was an issue with your password reset link
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
                
                <Button 
                  onClick={handleRequestNewReset}
                  className="w-full"
                >
                  Request New Password Reset
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />
      
      <div className="flex items-center justify-center p-4 min-h-[calc(100vh-64px)]">
        <div className="w-full max-w-md">
          <Card>
            <CardHeader className="text-center">
              <CardTitle className="text-2xl">Set New Password</CardTitle>
              <CardDescription>
                Please enter your new password below
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handlePasswordReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="password">New Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="password"
                      type="password"
                      placeholder="Enter new password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      id="confirmPassword"
                      type="password"
                      placeholder="Confirm new password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="pl-10"
                      required
                      minLength={6}
                    />
                  </div>
                  {password && confirmPassword && password !== confirmPassword && (
                    <p className="text-xs text-red-600">Passwords do not match</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading || password !== confirmPassword || !password}
                >
                  {loading ? (
                    <>
                      <Loader className="mr-2 h-4 w-4 animate-spin" />
                      Updating password...
                    </>
                  ) : (
                    'Update Password'
                  )}
                </Button>
              </form>

              {error && (
                <Alert className="mt-4" variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {success && (
                <Alert className="mt-4">
                  <AlertDescription>{success}</AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
